import { makeStyles, createStyles, Dialog } from '@material-ui/core';
import { useWindowWidthContext } from 'app/contexts/WindowWidthContext';
import React, { useCallback, useEffect, useState } from 'react';
import { desktopMinWidth } from 'utils/windowHelpers';
import MintDetails from './components/MintDetails';
import PictureZoom from './components/PictureZoom';
import StickyCTA from './components/StickyCTA';
import Lottie from 'lottie-react';
import loaderDark from './animation/loaderDark.json';
import { useHistory } from 'react-router';
import jwtDecode from 'jwt-decode';
import { mintNFT } from 'app/services/nftService';
import {
  defaultCertImageURL,
  defaultMintDetailsProps,
  getTokenMsg,
  tokenStatusType,
} from './components/constant';
import { MintDetailsObj, NftDetailsObj } from './components/types';
import Footer from '../../components/Footer';
import Page from '../Page';
import { Helmet } from 'react-helmet-async';
import { useUserContext } from '../../contexts/UserContext';
import HeaderV2 from '../../components/HeaderV2';
import { getCurrentWalletConnected } from '../../../utils/walletInteract';
import { trackEvent } from 'utils/analytics';

const defaultContractAdd = process.env.REACT_APP_NFT_CERT_CONTRACT_ADDRESS;

export const CertificateMintPage = () => {
  const useStyles = makeStyles(() =>
    createStyles({
      root: {
        minHeight: '100vh',
        backgroundColor: '#141521',
      },
      main: {
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: 32,
        padding: '32px 24px',
        flexDirection: 'column',
        // minHeight: '100vh',
        [desktopMinWidth]: {
          flexDirection: 'row',
          padding: '60px 0 140px 0',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 100,
        },
      },
      loading: {
        boxSizing: 'border-box',
        backgroundColor: '#282942',
        padding: '40px 24px',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'center',
        color: '#f2f2f2',
        fontWeight: 500,
        flexDirection: 'column',
        borderRadius: 4,
        fontSize: 18,
        [desktopMinWidth]: {
          width: 500,
          fontSize: 24,
        },
      },
      lineBreak: {
        width: 300,
        marginTop: 16,
        marginBottom: 24,
        [desktopMinWidth]: {
          marginTop: 24,
          marginBottom: 32,
        },
      },
      loadingLink: {
        color: '#f2f2f2',
        textDecoration: 'underline',
      },
      errorMsg: {
        maxWidth: 280,
        [desktopMinWidth]: {
          maxWidth: 400,
        },
      },
      backToLearnPortalLink: {
        textAlign: 'center',
        '& a': {
          color: '#f2f2f2',
          textDecoration: 'underline',
          fontSize: 12,
          marginTop: 12,
          [desktopMinWidth]: {
            fontSize: 14,
          },
        },
      },
    }),
  );
  const classes = useStyles();
  const { isDesktopView } = useWindowWidthContext();
  const defaultErrImg =
    'https://d2oi1rqwb0pj00.cloudfront.net/lesan/nasio/skeleton.png';

  const [tokenStatus, setTokenStatus] = useState<string>(
    tokenStatusType.PENDING,
  );

  const [mintDetails, setMintDetails] = useState<MintDetailsObj | null>(null);
  const [nftDetails, setNftDetails] = useState<NftDetailsObj | null>(null);
  const [isMinting, setIsMinting] = useState<boolean>(false);
  const [mintError, setMintError] = useState<string | null>('');
  const [backToLearningPortal, setBackToLearningPortal] = useState<boolean>(
    false,
  );

  const history = useHistory();
  const queryParams = new URLSearchParams(history.location.search);
  const token = queryParams.get('token');
  const redirectUrl = queryParams.get('redirectUrl');

  const walletAddress = mintDetails?.walletAddress ?? '';
  const courseName = mintDetails?.courseName ?? '';
  const studentName = mintDetails?.studentName;
  // const formattedWalletAddress = getEllipsisTextFormatted({
  //   str: walletAddress,
  // });
  const {
    walletAddress: currentWalletAddress,
    setWalletAddress,
  } = useUserContext();
  const isTokenError = [
    tokenStatusType.EXPIRED_TOKEN,
    tokenStatusType.INVALID_TOKEN,
    tokenStatusType.NO_TOKEN,
  ].includes(tokenStatus);
  const walletAddErr =
    currentWalletAddress === '' || currentWalletAddress !== walletAddress;
  const tokenErrorMsg = getTokenMsg({ status: tokenStatus });
  const mintImageLink = mintDetails?.imageLink;
  const certImageUrl = isTokenError
    ? defaultErrImg
    : mintImageLink
    ? mintImageLink.includes('https')
      ? mintImageLink
      : `${process.env.REACT_APP_IPFS_BASE_URL}/${mintImageLink}`
    : defaultCertImageURL;

  const validateToken = useCallback(() => {
    try {
      if (!token || token?.length === 0) {
        setTokenStatus(tokenStatusType.NO_TOKEN);
        return false;
      }
      const decodedTokenData: any = jwtDecode(token);
      const now = new Date();
      const exp = decodedTokenData.exp;

      if (now.getTime() / 1000 >= exp) {
        setTokenStatus(tokenStatusType.EXPIRED_TOKEN);
        return false;
      }

      const attributes = decodedTokenData.attributes;

      const mintDetailsObj = {
        walletAddress: decodedTokenData.walletAddress,
        learnerId: decodedTokenData.learnerId,
        learnerEmail: decodedTokenData.learnerEmail,
        courseCode: decodedTokenData.courseCode,
        imageLink: decodedTokenData.imageLink,
        courseName: attributes.course_name,
        classNumber: attributes.class_number,
        graduationDate: attributes.graduation_date,
        studentName: attributes.student_name,
        courseEnrolmentId: attributes.course_enrolment_id,
      };

      setMintDetails(mintDetailsObj);
      return true;
    } catch {
      setTokenStatus(tokenStatusType.INVALID_TOKEN);
      return false;
    }
  }, [token]);

  const onMintClick = async () => {
    try {
      setIsMinting(true);
      setMintError(null);
      setBackToLearningPortal(false);
      const {
        studentName,
        classNumber,
        graduationDate,
        learnerId,
        courseCode,
        courseEnrolmentId,
        learnerEmail,
      } = mintDetails || {};

      const payload = {
        imageURI: mintDetails?.imageLink,
        courseName,
        courseCode,
        classNumber: classNumber?.toString(),
        graduationDate,
        name: studentName,
        enrolmentId: courseEnrolmentId?.toString(),
        learnerId: learnerId?.toString(),
        learnerEmail,
        walletAddress: walletAddress,
      };

      // Call mint api
      const { data, error } = await mintNFT(payload, token);
      const path = window.location.pathname + window.location.search;
      trackEvent('mintCertificate', 'Mint NFT Cert', path, payload);
      if (error) {
        console.log(error.response);
        setMintError(error?.response?.data);
      }
      const { res } = data || {};
      if (res?.tokenId) {
        setMintError(
          `Minting takes about 15 minutes. You will be notified via ${learnerEmail} when you can view your certificate`,
        );
        setBackToLearningPortal(true);
      }
      if (!res) {
        setMintError('Transaction does not go through. Please contact admin');
      }
    } catch (e) {
      if (e instanceof Error) setMintError(e.message);
    } finally {
      setIsMinting(false);
    }
  };

  const getMintDetailsProps = () => {
    if (isTokenError) return defaultMintDetailsProps;

    if (nftDetails) {
      const tokenId = nftDetails?.tokenId;
      return {
        ctaText: 'View your NFT Certificate',
        ctaOnClick: () =>
          window.open(
            `${process.env.REACT_APP_FRONTEND_URL}/details/${defaultContractAdd}/${tokenId}`,
          ),
        title: courseName,
        headerText: 'Collection #000001',
        headerSecondaryText: `Token #${tokenId}`,
      };
    }

    if (mintDetails) {
      return {
        ctaText: 'Start minting',
        ctaOnClick: onMintClick,
        title: courseName,
        headerText: 'Collection #000001',
        headerSecondaryText: '10,000 NFT supply',
        textList: [
          'Authenticity of your certificate is traceable',
          'Easy to share on social platforms'
        ],
      };
    }
  };

  const stickyCtaProp = {
    ctaText: 'Mint my certificate',
    ctaOnClick: onMintClick,
  };

  const addWalletListener = () => {
    //@ts-ignore
    if (window.ethereum) {
      //@ts-ignore
      window.ethereum.on('accountsChanged', accounts => {
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
        } else {
          setWalletAddress('');
        }
      });
    }
  };

  const setInitialConnectedWallet = useCallback(async () => {
    const { address } = await getCurrentWalletConnected();

    setWalletAddress(address);
  }, []);

  useEffect(() => {
    setInitialConnectedWallet().then();
    addWalletListener();
  }, [token]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  return (
    <div className={classes.root}>
      <HeaderV2
        showNavlinks={false}
        headerTheme="blue"
        walletAddress={currentWalletAddress}
        showLogin={false}
      />
      <Helmet>
        <title>Mint Certificate</title>
        <meta
          name="description"
          content="Empowering Education using Web3/Blockchain technology. We are the technology arm of Nas Academy, whose mission is to create the world's best learning communities."
        />
        <meta
          name="description"
          content="Empowering Education using Web3/Blockchain technology. We are the technology arm of Nas Academy, whose mission is to create the world's best learning communities."
        />
        <meta
          property="og:image"
          content="https://s3.ap-southeast-1.amazonaws.com/image-assets.nasdaily.com/lesan/nasio/nasio_cover.png"
        />
      </Helmet>
      <Page>
        <div className={classes.main}>
          <PictureZoom
            url={certImageUrl}
            alt="nft certificate"
            width={isDesktopView ? 338 : 180}
            height={isDesktopView ? 520 : 295}
          />
          <MintDetails
            {...getMintDetailsProps()}
            isTokenError={isTokenError}
            walletAddress={walletAddress}
            studentName={studentName}
          />
          {!isDesktopView && <StickyCTA {...stickyCtaProp} />}
          <Dialog open={isMinting}>
            <div className={classes.loading}>
              <span>Transaction in progress...</span>
              <Lottie
                animationData={loaderDark}
                loop={true}
                autoplay={true}
                style={{
                  width: 50,
                  height: 50,
                }}
              />
            </div>
          </Dialog>
          <Dialog open={isTokenError}>
            <div className={classes.loading}>
              <span className={classes.errorMsg}>{tokenErrorMsg}</span>
              <div className={classes.backToLearnPortalLink}>
                <a
                  href={
                    redirectUrl
                      ? redirectUrl
                      : process.env.REACT_APP_LEARNING_PORTAL_URL
                  }
                >
                  Back to Learning Portal
                </a>
              </div>
            </div>
          </Dialog>
          {/*<Dialog open={walletAddErr && !isTokenError}>*/}
          {/*  <div className={classes.loading}>*/}
          {/*    <span>*/}
          {/*      Make sure you logged in with the same Metamask account as the*/}
          {/*      learning portal to transfer token to the right address*/}
          {/*    </span>*/}
          {/*  </div>*/}
          {/*</Dialog>*/}
          <Dialog
            open={!!mintError}
            onClose={() => {
              if (!backToLearningPortal) setMintError('');
            }}
          >
            <div className={classes.loading}>
              <span>{mintError}</span>
              {backToLearningPortal && (
                <div className={classes.backToLearnPortalLink}>
                  <a
                    href={
                      redirectUrl
                        ? redirectUrl
                        : process.env.REACT_APP_LEARNING_PORTAL_URL
                    }
                  >
                    Back to Learning Portal
                  </a>
                </div>
              )}
            </div>
          </Dialog>
        </div>
        <Footer blueTheme={true} />
      </Page>
    </div>
  );
};
